import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button.tsx';
import { Link, useNavigate } from 'react-router';
import { PlantSchema, TPlant } from '@/features/plants/schemas/plants.schema.ts';
import { CreatePlantFormNavigator } from '@/features/plants/components/plant-management/shared/form-navigator.tsx';
import { Suspense, useCallback, useEffect } from 'react';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useGetPlantQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant.ts';
import { useUpdatePlant } from '@/features/plants/hooks/mutations/[plantId]/use-update-plant.ts';
import { useGetPlantDevicesQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-devices-plant.ts';
import { STEPS_EDIT } from './plant-form-edit-steps.ts';
import { hasPermission } from '@/features/users/schemas/users.permissions.ts';
import { useSession } from '@/services/providers/use-session.ts';
import { LoaderIcon } from 'lucide-react';

const findFirstErrorPath = (errors: unknown, prefix = ''): string | null => {
  if (!errors || typeof errors !== 'object') return null;

  for (const key of Object.keys(errors)) {
    const value = (errors as Record<string, unknown>)[key];
    if (!value || typeof value !== 'object') continue;

    const path = prefix ? `${prefix}.${key}` : key;
    const record = value as Record<string, unknown>;

    if ('message' in record && 'ref' in record) return path;

    const nested = findFirstErrorPath(record, path);
    if (nested) return nested;
  }

  return null;
};

export const EditPlantForm = ({ plantId }: { plantId: string }) => {
  const navigate = useNavigate();
  const { userId, role } = useSession();

  const { data, isLoading } = useGetPlantQuery({ plantId });

  const { data: plantDevices, isLoading: isDevicesLoading } = useGetPlantDevicesQuery({
    plantId,
  });

  const form = useForm({
    resolver: zodResolver(PlantSchema),
    mode: 'onChange',
    defaultValues: {
      plantId: data?.plantId ?? '',
      basicDetails: {
        name: data?.basicDetails.name ?? '',
        type: data?.basicDetails.type ?? '',
        status: data?.basicDetails.status ?? 'Maintenance',
        shortDescription: data?.basicDetails.shortDescription ?? '',
      },
      siteInformation: {
        image: data?.siteInformation?.image ?? '',
        country: {
          label: '',
          value: '',
        },
        state: {
          label: '',
          value: '',
        },
        city: {
          label: '',
          value: '',
        },
        coordinates: {
          latitude: '',
          longitude: '',
        },
        siteAddress: '',
        siteArea: '',
        siteAccess: {
          label: '',
          value: '',
        },
      },
      technicalSpecifications: {
        setupType: data?.technicalSpecifications.setupType,
      },
      equipmentAndConnectionDetails: {
        devices: plantDevices ?? [],
      },
      financialInformation: {
        tariffRate: '',
        expectedROI: '',
        operationalCosts: '',
      },
      contactDetails: {
        owner: {
          firstName: '',
          lastName: '',
        },
        ownerEmail: '',
        ownerPhoneNumber: '',
        installer: '',
        installerEmail: '',
        installerPhoneNumber: '',
      },
      dataCollectionInterval: data?.dataCollectionInterval ?? '',
      createdAt: data?.createdAt ?? '',
      updatedAt: data?.updatedAt ?? '',
    },
  });

  useEffect(() => {
    if (data && !isLoading && !isDevicesLoading) {
      form.reset({
        ...data,
        equipmentAndConnectionDetails: {
          devices: plantDevices ?? [],
        },
      });
      form.trigger('equipmentAndConnectionDetails.devices');
    }
  }, [data, plantDevices, isLoading, isDevicesLoading]);

  const handleReset = useCallback(() => {
    if (!data) return;

    form.reset({
      ...data,
      equipmentAndConnectionDetails: {
        devices: plantDevices ?? [],
      },
    });
  }, [data, plantDevices, form]);

  const updatePlantMutation = useUpdatePlant({
    onSuccess: () => {
      navigate('/plants');
    },
  });

  const onSubmit = (data: TPlant) => {
    updatePlantMutation.mutate(data);
  };

  const onInvalid = (errors: FieldErrors<TPlant>) => {
    const firstPath = findFirstErrorPath(errors);
    if (!firstPath) return;

    const sectionId = firstPath.split('.')[0];
    const target =
      (document.querySelector(`[name="${firstPath}"]`) as HTMLElement | null) ??
      document.getElementById(sectionId);

    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (target instanceof HTMLElement && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }
  };

  if (!data?.plantId && !isLoading)
    return (
      <>
        Oops... No plant found with the id of <b>{plantId}</b>
      </>
    );

  return (
    <div className="@container">
      <div className="grid w-full max-w-7xl grid-cols-1 gap-2 pb-10 @2xl:grid-cols-[1fr_250px] @2xl:gap-4">
        <div className="flex flex-1 flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-bold">Edit Plant</h2>
          </div>
          <div className="">
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
                <div className="flex flex-col gap-4">
                  {isLoading && (
                    <div className="flex h-20 items-center justify-center">
                      <LoadingSpinner size={50} />
                    </div>
                  )}
                  {!isLoading && (
                    <Suspense
                      fallback={
                        <div className="flex min-h-svh justify-center">
                          <LoadingSpinner size={50} />
                        </div>
                      }
                    >
                      {STEPS_EDIT.map((S) => (
                        <S.Element key={S.id} showTitle={true} />
                      ))}
                    </Suspense>
                  )}

                  {!isLoading && (
                    <div className="flex items-center justify-end gap-2 sm:gap-4">
                      <Button
                        disabled={updatePlantMutation.isPending}
                        variant="ghost"
                        type="button"
                        asChild
                      >
                        <Link to="/plants">Cancel</Link>
                      </Button>

                      {hasPermission({ userId, role }, 'plants', 'update') && (
                        <Button
                          disabled={updatePlantMutation.isPending}
                          variant="secondary"
                          type="button"
                          onClick={handleReset}
                          className="cursor-pointer"
                        >
                          Reset
                        </Button>
                      )}

                      {hasPermission({ userId, role }, 'plants', 'update') && (
                        <Button
                          disabled={updatePlantMutation.isPending}
                          type="button"
                          className="cursor-pointer gap-2"
                          onClick={() => {
                            form.handleSubmit(onSubmit, onInvalid)();
                          }}
                        >
                          {updatePlantMutation.isPending && (
                            <LoaderIcon
                              className="size-4 animate-spin text-white"
                              aria-hidden="true"
                            />
                          )}
                          {updatePlantMutation.isPending ? 'Submitting...' : 'Submit'}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
        <div className="relative h-full">
          <div className="sticky top-0">
            <CreatePlantFormNavigator
              steps={STEPS_EDIT}
              currentStepIndex={STEPS_EDIT.length}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
